import { useEffect, useMemo } from "react";
import { AgGridReact, Button, Card, Flex, Grid, QueryLoader, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { Colors, Sizes } from "@jsq/ds/styles";
import { useFeatureFlag } from "@jsq/flags-react";
import { useGetTransferEntityPreviewGlSyncQuery } from "~/graphql/supergraphHooks";
import { getCapitalActivityLabel } from "~/pages/FundAccounting/types";
import { formatDate } from "~/pages/FundAccounting/utils/helpers";
import { toFederationGlobalId } from "~/utils/graphql-helpers";

type Props = {
  onBack: () => void;
  onSync: () => void;
  onLoaded?: (glDates: any[]) => void;
  entityId: number;
  arenaId: number;
  endDate: string;
  includeAllTransactions: boolean;
  startDate: string | null;
};

const getTransactionTypeNameMap = (
  showCapitalActivityLabelCanopy: boolean,
): {
  [key in "capital_call" | "capital_transaction" | "distribution" | "net_income"]: string;
} => ({
  capital_call: "Capital call",
  capital_transaction: getCapitalActivityLabel(showCapitalActivityLabelCanopy),
  distribution: "Distribution",
  net_income: "Net income",
});
// Define transaction type order
const transactionTypeOrder = {
  capital_call: 0,
  capital_transaction: 1,
  distribution: 2,
  net_income: 3,
};

const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
  {
    field: "glDate",
    headerName: "GL transfer date",
    cellRenderer: "agGroupCellRenderer",
    maxWidth: 175,
  },
  { field: "description", headerName: "Description" },
  { field: "investor", headerName: "Investor" },
  {
    field: "navToTransfer",
    headerName: "NAV to be transferred",
    valueFormatter: (params) => {
      return params.value < 0 ? `($${Math.abs(params.value)})` : `$${params.value}`;
    },
  },
];

const detailColumnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
  { field: "category", headerName: "Category" },
  {
    field: "amount",
    headerName: "Amount",
    valueFormatter: (params) => {
      return params.value < 0 ? `($${Math.abs(params.value)})` : `$${params.value}`;
    },
  },
];

const masterGridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  autoSizeStrategy: {
    type: "fitGridWidth",
  },
  defaultColDef: {
    wrapText: true,
    autoHeight: true,
    autoHeaderHeight: true,
    flex: 1,
  },
  suppressCellFocus: true,
  rowHeight: 44,
  domLayout: "autoHeight",
  masterDetail: true,
};

const detailCellRendererParams = () => {
  return {
    detailGridOptions: {
      autoSizeStrategy: {
        type: "fitGridWidth",
      },
      columnDefs: detailColumnDefs,
      rowHeight: 44,
      domLayout: "autoHeight",
    },
    getDetailRowData: (params: any) => {
      params.successCallback(params.data.transactions);
    },
  };
};

type TransactionRow = {
  glDate: string;
  description: string;
  investor: string;
  navToTransfer: number;
  transactions: {
    category: string;
    amount: number;
  }[];
};

const isRowMaster = (dataItem: TransactionRow) => {
  return dataItem.transactions.length > 0;
};

const TransferSummaryGrid = ({
  onBack,
  onSync,
  entityId,
  arenaId,
  endDate,
  startDate,
  onLoaded,
  includeAllTransactions,
}: Props) => {
  const globalArenaId = toFederationGlobalId("ArenaObjectType", arenaId);
  const enableResyncTransfer = useFeatureFlag("resyncTransfer", false);
  const showCapitalActivityLabelCanopy = useFeatureFlag(
    "showCapitalActivityLabelCanopy",
    false,
  );
  const transactionTypeNameMap = useMemo(
    () => getTransactionTypeNameMap(showCapitalActivityLabelCanopy),
    [showCapitalActivityLabelCanopy],
  );
  const glSyncResponse = useGetTransferEntityPreviewGlSyncQuery({
    variables: {
      syncInput: {
        includeAllTransactions: enableResyncTransfer && includeAllTransactions,
        arenaId: globalArenaId,
        entityId: entityId,
        endDate,
        startDate,
      },
    },

    fetchPolicy: "network-only",
  });

  const { data, loading } = glSyncResponse;

  const syncedTransferBatchSummaries = data?.entityTransferPreviewGlSync;

  const rowData = useMemo(() => {
    const data: TransactionRow[] = [];
    const sortedDate = syncedTransferBatchSummaries
      ? [...syncedTransferBatchSummaries].sort((a, b) => {
          return new Date(a.glBatchDate).getTime() - new Date(b.glBatchDate).getTime();
        })
      : undefined;
    sortedDate?.forEach((batch) => {
      const glDate = formatDate(batch.glBatchDate);

      const description = batch.glBatchDescription;
      batch.positions.forEach((position, idx) => {
        const investor = position.accountName;
        const navToTransfer = position.transactionTypes.reduce(
          (acc, txnType) => acc + Number(txnType.amount),
          0,
        );

        // Sort by predefined order instead of date
        const sortedTransactionTypes = [...position.transactionTypes].sort((a, b) => {
          return (
            transactionTypeOrder[a.type as keyof typeof transactionTypeOrder] -
            transactionTypeOrder[b.type as keyof typeof transactionTypeOrder]
          );
        });

        data.push({
          glDate: idx === 0 ? glDate : "",
          description: idx === 0 ? description : "",
          investor,
          navToTransfer,
          transactions: sortedTransactionTypes.map((txnType) => ({
            category:
              transactionTypeNameMap[txnType.type as keyof typeof transactionTypeNameMap],
            amount: Number(txnType.amount),
          })),
        });
      });
    });

    return data;
  }, [syncedTransferBatchSummaries, transactionTypeNameMap]);

  useEffect(() => {
    if (syncedTransferBatchSummaries) {
      const sortedData = [...syncedTransferBatchSummaries].sort(
        (a, b) => new Date(a.glBatchDate).getTime() - new Date(b.glBatchDate).getTime(),
      );
      onLoaded?.(sortedData);
    }
  }, [syncedTransferBatchSummaries]);
  return (
    <Flex flexDirection="column" style={{ width: "100%" }} gap={Sizes.MD}>
      <QueryLoader result={glSyncResponse}>
        <Card title="Transfers transaction summary">
          <div
            className="ag-theme-alpine"
            style={{
              width: "100%",
            }}
          >
            {rowData.length > 0 ? (
              <Grid
                className="GridWrapper"
                columnDefs={columnDefs}
                detailCellRendererParams={detailCellRendererParams}
                gridOptions={masterGridOptions}
                masterDetail={true}
                isRowMaster={isRowMaster}
                rowData={rowData}
              />
            ) : (
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={Sizes.SM}
                style={{ height: "216px" }}
              >
                <Icon type="inbox" color={Colors.Neutral600} />
                <Text variant="secondary-md">
                  There are no transfers to sync in your selected date range
                </Text>
              </Flex>
            )}
          </div>
        </Card>

        <Flex justifyContent="flex-end" gap={Sizes.MD}>
          <Button variant="neutral" onClick={onBack}>
            Back
          </Button>
          <Button isLoading={loading} variant="primary" onClick={onSync}>
            Start sync
          </Button>
        </Flex>
      </QueryLoader>
    </Flex>
  );
};

const ReviewAndSync = ({
  onBack,
  onSync,
  entityId,
  arenaId,
  endDate,
  startDate,
  onLoaded,
  includeAllTransactions,
}: Props) => {
  return (
    <ColumnContainer>
      <Column size="xl">
        <TransferSummaryGrid
          onBack={() => onBack()}
          onSync={() => onSync()}
          entityId={entityId}
          arenaId={arenaId}
          endDate={endDate}
          startDate={startDate}
          onLoaded={onLoaded}
          includeAllTransactions={includeAllTransactions}
        />
      </Column>
    </ColumnContainer>
  );
};

export default ReviewAndSync;
